import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Label, Tabs, TabsRef } from 'flowbite-react';
import { MdAdd } from 'react-icons/md';
import Datepicker from 'react-tailwindcss-datepicker';
import { DateValueType } from 'react-tailwindcss-datepicker/dist/types';

import AddDurations from './AddDurations';
import DateSlotsList from './DateSlotsList';

import moment from 'moment';
import { DateValidtyRange, UserAvailabilityInput } from 'types';
import { DURATION_OPTIONS, convertedDateFromDateType } from 'shared-utils';

type Props = {
	isOnboarding?: boolean;
	availabilities: UserAvailabilityInput[];
	setAvailabilities?: (availabilities: UserAvailabilityInput[]) => void;
};

const MentorAvailabilitiesInput = ({ isOnboarding, availabilities = [], setAvailabilities = () => {} }: Props) => {
	const tabsRef = useRef<TabsRef>(null);
	const [activeTab, setActiveTab] = useState<number>(0);
	const [selectedValidityRange, setSelectedValidityRange] = useState<DateValidtyRange>({
		startDate: moment().add(1, 'days').toDate(),
		endDate: moment().add(7, 'days').toDate(),
	});
	const [durationTabs, setDurationTab] = useState<number[]>([]);

	const handleValidityChange = (value: DateValueType) => {
		if (!value) {
			const [validFrom, validTill] = [moment().toDate(), moment().add(7, 'days').toDate()];
			setSelectedValidityRange({ startDate: validFrom, endDate: validTill });
		} else {
			let { startDate, endDate } = value;
			[startDate, endDate] = [convertedDateFromDateType(startDate), convertedDateFromDateType(endDate)];
			setSelectedValidityRange({
				startDate,
				endDate,
			});
		}
	};

	const handleOnClearUnbookedSlotsInRange = () => {
		// filter availability to remove unbooked slots for the day.
		let dateStartTime = moment(selectedValidityRange.startDate).startOf('date');
		let dateEndTime = moment(selectedValidityRange.endDate).endOf('date');

		let unchangedAvailabilities = availabilities.filter(a => {
			if (a.isBooked) return true;
			else {
				let availabiltyStartDateTime = moment(a.startTime);
				let availabiltyEndDateTime = moment(a.endTime);

				return availabiltyStartDateTime.isAfter(dateEndTime) || availabiltyEndDateTime.isSameOrBefore(dateStartTime);
			}
		});

		// update
		setAvailabilities(unchangedAvailabilities);
	};

	const onAddDurationTab = (duration: number) => {
		let newTabs = [...durationTabs, duration].sort((a, b) => a - b);
		setDurationTab(newTabs);
	};

	useEffect(() => {
		let uniqueDurations = new Set(availabilities.map(a => a.duration));
		let newTabs = Array.from(uniqueDurations).sort((a, b) => a - b);
		setDurationTab(newTabs);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availabilities]);
	return (
		<div className={`${isOnboarding ? 'hukumu-max-width-680' : ''}`}>
			<div className="grid grid-cols-2 gap-2 md:gap-[20px]">
				<div>
					<div>
						<Label>{'Validity Range '}</Label>
						<span className="text-[red]">*</span>
					</div>
					<div className="">
						<Datepicker
							value={selectedValidityRange}
							minDate={moment().subtract(1, 'day').toDate()}
							onChange={handleValidityChange}
							popoverDirection="down"
						/>
					</div>
				</div>

				<div>
					<Label>{'Quick Actions '}</Label>
					<div className="flex flex-wrap gap-2">
						<Button color="purplePlaced" onClick={handleOnClearUnbookedSlotsInRange}>
							Clear unbooked slots in range
						</Button>
					</div>
				</div>
			</div>
			<Tabs.Group
				aria-label="Duration tabs"
				// style="underline"
				className="px-0"
				ref={tabsRef}
				onActiveTabChange={tab => setActiveTab(tab)}
			>
				{durationTabs.map((duration: number, index) => {
					return (
						<Tabs.Item
							title={<div className="flex items-center justify-center gap-2">Duration: {duration}</div>}
							key={duration}
							active={activeTab === index}
						>
							<div className="grid gap-2">
								<div className="flex flex-wrap gap-2">
									<Badge color="failure">Booked Slots</Badge>
									<Badge color="success">Available</Badge>
									<Badge color="warning">Not Available</Badge>
								</div>

								<DateSlotsList
									duration={duration}
									startDate={selectedValidityRange.startDate}
									endDate={selectedValidityRange.endDate}
									availabilities={availabilities}
									setAvailabilities={setAvailabilities}
								/>
							</div>
						</Tabs.Item>
					);
				})}
				<Tabs.Item title={<MdAdd />}>
					<AddDurations
						availableDurations={DURATION_OPTIONS.filter(option => !durationTabs.includes(option.value)).map(
							o => o.value,
						)}
						onAddDurationTab={onAddDurationTab}
					/>
				</Tabs.Item>
			</Tabs.Group>
		</div>
	);
};

export default MentorAvailabilitiesInput;
